import React, { FC, memo, useState, useRef, useEffect } from "react";
import { RichText } from "@montana/shared/rich-text";
import {
    Form,
    FormTextInput,
    FormSelect,
    FormTextArea,
    FormCheckbox
} from "@montana/shared/form";
import { useTranslations } from "@montana/containers/translations";
import {
    required,
    combineValidators,
    email
} from "@montana/shared/form/validation";
import { Button } from "@montana/shared/button";
import { Translate } from "@montana/shared/translate";
import { IM171ContactMontana } from "@montana/models/generated/cms.interface";
import { subscribe } from "@montana/services/sales-form";
import { IFormValues, IFormRef } from "@montana/shared/form/form.component";
import { Appear } from "@montana/shared/appear";
import { IFieldOptionValue } from "@montana/shared/form/field/field";
import { useSettings } from "@montana/containers/settings/settings.container";
import { useConnectedServiceFunctions } from "@montana/shared/use-connected-service-functions";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";
import clsx from "clsx";
import { M171ContactMontanaBlockModal } from "./m17.1-contact-montana-block-modal.component";

export const M171ContactMontanaBlock: FC<IM171ContactMontana> = memo(
    (props: IM171ContactMontana) => {
        const requestTypeInput = "requestType";
        const companyNameInput = "companyName";
        const fullNameInput = "fullName";
        const emailInput = "email";
        const customerTypeInput = "customerType";
        const commentsInput = "comments";
        const emailPermissionInput = "acceptTerms";

        const {
            title,
            text,
            backgroundColorHex,
            requestOptions,
            termsAndPrivacyPolicy
        } = props;
        const [submitting, setSubmitting] = useState<boolean>(false);
        const [requestSent, setRequestSent] = useState<boolean | undefined>();
        const { market } = useSettings();
        const { customerTypes } = useDynamicSettings();
        const translate = useTranslations();
        const [cSubscribe] = useConnectedServiceFunctions(subscribe);

        const requestType = !!requestOptions?.length
            ? requestOptions[0]
            : undefined;
        const customerType = !!customerTypes?.length
            ? customerTypes[0].key
            : undefined;
        const formRef = useRef<IFormRef>(null);

        const [mainSectionWidth, setMainSectionWidth] = useState<number>(0);

        useEffect(() => {
            const element = document.getElementById("main-section");
            if (element) {
                setMainSectionWidth(element.clientWidth);
            }
        }, []);

        async function handleSubmit(values: IFormValues) {
            setSubmitting(true);

            try {
                const response = await cSubscribe({
                    companyName: values[companyNameInput],
                    customerType: values[customerTypeInput] || customerType,
                    clientName: values[fullNameInput],
                    clientEmail: values[emailInput],
                    requestType: values[requestTypeInput] || requestType,
                    optionalText: values[commentsInput],
                    emailPermission: Boolean(values[emailPermissionInput]),
                    clientMarket: market?.marketId,
                    pageUrl: document.location.href
                });

                if (response.ok) {
                    setRequestSent(true);
                    formRef.current?.getForm()?.resetForm({
                        [requestTypeInput]: requestType,
                        [companyNameInput]: "",
                        [fullNameInput]: "",
                        [emailInput]: "",
                        [customerTypeInput]: customerType,
                        [commentsInput]: ""
                    });
                } else {
                    throw new Error("Request unsuccessful");
                }
            } catch (error) {
                setRequestSent(false);
            } finally {
                setSubmitting(false);
                setTimeout(() => {
                    setRequestSent(undefined);
                }, 3000);
            }
        }

        // This block is considered a modal if smaller than lg
        if (mainSectionWidth < 864) {
            return <M171ContactMontanaBlockModal {...props} />;
        }

        return (
            <Appear>
                <section
                    className="contact-montana-block"
                    style={{ backgroundColor: backgroundColorHex }}
                    id="main-section"
                >
                    <div className={`contact-montana-block__content`}>
                        <div className={`contact-montana-block-column`}>
                            {title && (
                                <RichText
                                    className="contact-montana-block__title"
                                    text={title}
                                    marginOnLastChild={!!text}
                                />
                            )}
                            {text && (
                                <RichText
                                    className="contact-montana-block__text"
                                    text={text}
                                />
                            )}
                        </div>
                        <div className="contact-montana-block-form">
                            <Form
                                onSubmit={handleSubmit}
                                initialValues={{}}
                                ref={formRef}
                            >
                                {() => (
                                    <>
                                        {!!requestOptions?.length && (
                                            <FormSelect
                                                name={requestTypeInput}
                                                options={requestOptions.map<
                                                    IFieldOptionValue
                                                >(c => ({ text: c, value: c }))}
                                                defaultValue={requestType}
                                                label={translate(
                                                    t => t.forms.requestType,
                                                    "[Request type]"
                                                )}
                                                inlineLabel={true}
                                                required
                                            />
                                        )}
                                        <FormTextInput
                                            type="text"
                                            name={companyNameInput}
                                            label={translate(
                                                t => t.forms.companyName,
                                                "[Company name]"
                                            )}
                                            validate={required(
                                                translate(
                                                    t => t.forms.fieldRequired,
                                                    "[This field is required]"
                                                )
                                            )}
                                            required
                                        />
                                        <FormTextInput
                                            type="text"
                                            name={fullNameInput}
                                            label={translate(
                                                t => t.forms.fullName,
                                                "[Full name]"
                                            )}
                                            validate={required(
                                                translate(
                                                    t => t.forms.fieldRequired,
                                                    "[This field is required]"
                                                )
                                            )}
                                            required
                                        />
                                        <FormTextInput
                                            type="email"
                                            name={emailInput}
                                            label={translate(
                                                t => t.forms.email,
                                                "[E-mail]"
                                            )}
                                            validate={combineValidators(
                                                required(
                                                    translate(
                                                        t =>
                                                            t.forms
                                                                .fieldRequired,
                                                        "[This field is required]"
                                                    )
                                                ),
                                                email(
                                                    translate(
                                                        t =>
                                                            t.forms
                                                                .invalidEmail,
                                                        "[Invalid email]"
                                                    )
                                                )
                                            )}
                                            required
                                        />
                                        {!!customerTypes?.length && (
                                            <FormSelect
                                                name={customerTypeInput}
                                                options={customerTypes.map<
                                                    IFieldOptionValue
                                                >(c => ({
                                                    text: c.displayName,
                                                    value: c.key!
                                                }))}
                                                defaultValue={"Architect"}
                                                label={translate(
                                                    t => t.forms.customerType,
                                                    "[Customer type]"
                                                )}
                                                inlineLabel={true}
                                                required
                                            />
                                        )}
                                        <FormTextArea
                                            className="contact-montana-block__comment-field"
                                            name={commentsInput}
                                            placeholder={translate(
                                                t => t.forms.comment,
                                                "[Comments]"
                                            )}
                                        />
                                        <FormCheckbox
                                            className="newsletter-sign-up__accept-terms"
                                            name={emailPermissionInput}
                                        >
                                            {termsAndPrivacyPolicy && (
                                                <RichText
                                                    className="newsletter-sign-up__accept-terms-text"
                                                    text={termsAndPrivacyPolicy}
                                                    disableAppear
                                                />
                                            )}
                                        </FormCheckbox>
                                        <Button
                                            className="contact-montana-block__submit-button"
                                            submitButton
                                            isLoading={submitting}
                                        >
                                            <Translate
                                                value="forms.sendRequest"
                                                defaultValue="[Send request]"
                                            />
                                        </Button>
                                        <div
                                            className={clsx(
                                                "contact-montana-block-form__feedback",
                                                requestSent !== undefined &&
                                                    "contact-montana-block-form__feedback--active"
                                            )}
                                        >
                                            <span
                                                className={
                                                    requestSent
                                                        ? "contact-montana-block-form__subscription-success"
                                                        : "contact-montana-block-form__subscription-failed"
                                                }
                                            >
                                                {requestSent ? (
                                                    <Translate
                                                        value="forms.confirmationMessage"
                                                        defaultValue="[You have succesfully sent the request!]"
                                                    />
                                                ) : (
                                                    requestSent !==
                                                        undefined && (
                                                        <Translate
                                                            value="forms.errorMessage"
                                                            defaultValue="[Failed to send the request]"
                                                        />
                                                    )
                                                )}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </Form>
                        </div>
                    </div>
                </section>
            </Appear>
        );
    }
);
